import * as React from 'react';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Slide from '@mui/material/Slide';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { toast } from 'react-toastify';
import * as Device from 'react-device-detect';

import { useSession } from '@hooks';
import { refetchEventsVar } from '@context';
import {
  useGetEventDetailedQuery,
  GetEventDetailedQuery,
  useUpdateEventStatusMutation,
  EventStatusEnum,
} from '@graphql';

interface DeleteEventProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleDeleted: () => void;
  id: string;
}

export function DeleteEvent(props: Readonly<DeleteEventProps>) {
  const { open, setOpen, handleDeleted, id } = props;
  const { account } = useSession();
  const [canEditEvent, setCanEditEvent] = React.useState(false);
  const theme = useTheme();
  const { t } = useTranslation('field');

  const { data, loading } = useGetEventDetailedQuery({
    variables: { id },
  });

  const [deleteEvent, { loading: deleting }] = useUpdateEventStatusMutation({
    variables: {
      id,
      status: EventStatusEnum.Deleted,
    },
    onCompleted(event) {
      refetchEventsVar(true);
      toast(t('event.toast.delete.success'), { type: 'success' });
      handleClose();
    },
    onError(error) {
      toast.error(t('event.toast.delete.error'));
      handleClose();
    },
  });

  const handleClose = () => {
    setOpen(false);
  };

  React.useEffect(() => {
    if (data?.event) {
      setCanEditEvent(canEdit(data.event.creator, account?.id));
    }
  }, [data]);

  return (
    <Dialog
      open={open}
      scroll="body"
      maxWidth="sm"
      fullWidth
      fullScreen={Device.isMobileOnly}
      slots={{
        transition: Transition,
      }}
    >
      {loading && (
        <Stack height="100%" alignItems="center" justifyContent="center" p={3}>
          <CircularProgress />
        </Stack>
      )}
      {!data && (
        <Stack height="100%" alignItems="center" justifyContent="center" p={3}>
          <CircularProgress />
        </Stack>
      )}
      {data && (
        <Stack minHeight="90dvh" sx={{ position: 'relative' }} justifyContent="space-between">
          <Stack direction="row" justifyContent="space-between" alignItems="center" p={3}>
            <Typography variant="h6">{data.event.title}</Typography>
            <IconButton onClick={handleClose}>
              <theme.icons.close />
            </IconButton>
          </Stack>

          <DialogContent
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {canEditEvent ? (
              <Stack height="100%" spacing={2} alignItems="center" justifyContent="space-between">
                <Typography variant="body2" align="center">
                  {t('delete.You are about to delete this event.', { ns: 'event' })}
                </Typography>
                <Stack
                  spacing={2}
                  p={2}
                  sx={{ backgroundColor: theme.palette.background.default, borderRadius: 1 }}
                >
                  <Typography variant="h6" align="center">
                    {data.event.title}
                  </Typography>
                  <Stack spacing={1} p={2}>
                    <Typography variant="body2" align="center" color="text.secondary">
                      Du {moment(data.event.from).format('LLL')}
                    </Typography>
                    <Typography variant="body2" align="center" color="text.secondary">
                      Au {moment(data.event.to).format('LLL')}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack spacing={2}>
                  <Alert severity="info">
                    {t(
                      'delete.You can always restore it within 30 days. After this period, the event will be permanently deleted and cannot be restored.',
                      { ns: 'event' }
                    )}
                  </Alert>
                  <Typography variant="body2" align="center">
                    {t('delete.Do you confirm the deletion of this event?', { ns: 'event' })}
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Stack height="100%" alignItems="center" justifyContent="center">
                <Alert severity="error">
                  {t('delete.You do not have the rights to delete this event.', { ns: 'event' })}
                </Alert>
              </Stack>
            )}
          </DialogContent>

          <DialogActions>
            <Stack direction="row" spacing={2} p={3} width="100%" justifyContent="space-between">
              {canEditEvent && (
                <Button
                  loading={deleting}
                  fullWidth={Device.isMobile}
                  onClick={deleteEvent}
                  variant="outlined"
                  color="error"
                >
                  {t('confirm', { ns: 'cta' })}
                </Button>
              )}
              <Button onClick={handleClose} color="uncolored" variant="contained" fullWidth={Device.isMobile}>
                {t(canEditEvent ? 'cancel' : 'close', { ns: 'cta' })}
              </Button>
            </Stack>
          </DialogActions>
        </Stack>
      )}
    </Dialog>
  );
}

function canEdit(creator: GetEventDetailedQuery['event']['creator'], accountId?: string) {
  switch (creator.__typename) {
    case 'Consumer':
    case 'Spot':
    case 'Maker':
      return accountId === creator.id;
    default:
      return false;
  }
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});
